// Copyright 2023 Sorama B.V.
import { CAMName, IWorkflowMode } from "./IWorkflowMode";

// This user utilizes Portal to analyze measurements, manage documents and generate Special Measurements Reports.
export class AcousticDesignCycleLiteWFM extends IWorkflowMode {
    public readonly name = "Acoustic Design Cycle Lite";
    public readonly CAMSet: Set<CAMName> = new Set(["CAMiV64"]);
    public readonly CAMSetName = "Sorama CAM iV64";

    public readonly canDoRealTimeRecording = false;
    public readonly canDoMeasurementAnalysis = true;
    public readonly canDoReporting = true;

    public readonly isIndustrial = false;
    public readonly isBuilding = true;
}

// The user utilizes Portal regularly, following Portal flow, from Stream to Presentation.
export class AcousticDesignCycleWFM extends IWorkflowMode {
    public readonly name = "Acoustic Design Cycle";
    public readonly CAMSet: Set<CAMName> = new Set(["CAM64", "CAM1K"]);
    public readonly CAMSetName = "Acoustic Design Cycle (Sorama CAM64 or CAM1K)";

    public readonly canDoRealTimeRecording = true;
    public readonly canDoMeasurementAnalysis = true;
    public readonly canDoReporting = false;

    public readonly isIndustrial = false;
    public readonly isBuilding = false;
}

// This user uses all the functionality of Portal.
export class FullWFM extends IWorkflowMode {
    public readonly name = "Full Mode";
    public readonly CAMSet: Set<CAMName> = new Set([
        "CAM64",
        "CAM1K",
        "CAMiV64",
        "CAMiV64Ex",
        "CAMiV64s",
        "L642",
        "L642Ex",
    ]);

    public readonly CAMSetName = "All Sorama Products";

    public readonly canDoRealTimeRecording = true;
    public readonly canDoMeasurementAnalysis = true;
    public readonly canDoReporting = true;

    public readonly isIndustrial = false;
    public readonly isBuilding = false;
}

// This user uses all the functionality of Portal.
export class IndustrialHandheldWFM extends IWorkflowMode {
    public readonly name = "Industrial Handheld";
    public readonly CAMSet: Set<CAMName> = new Set(["CAMiV64s", "CAMiV64Ex"]);
    public readonly CAMSetName = "Industrial (Sorama CAM iV64s or CAM iV64Ex)";

    public readonly canDoRealTimeRecording = false;
    public readonly canDoMeasurementAnalysis = false;
    public readonly canDoReporting = true;

    public readonly isIndustrial = true;
    public readonly isBuilding = false;
}

export class IndustrialFixedWFM extends IWorkflowMode {
    public readonly name = "Industrial Fixed";
    public readonly CAMSet: Set<CAMName> = new Set(["L642", "L642Ex", "L642V+"]);
    public readonly CAMSetName = "Industrial (Sorama L642, L642Ex or L642V+)";

    public readonly canDoRealTimeRecording = false;
    public readonly canDoMeasurementAnalysis = false;
    public readonly canDoReporting = true;

    public readonly isIndustrial = true;
    public readonly isBuilding = false;
}

export abstract class WorkflowModes {
    public static readonly AcousticDesignCycleLite = new AcousticDesignCycleLiteWFM();
    public static readonly AcousticDesignCycle = new AcousticDesignCycleWFM();
    public static readonly FullWFM = new FullWFM();
    public static readonly IndustrialHandheld = new IndustrialHandheldWFM();
    public static readonly IndustrialFixed = new IndustrialFixedWFM();

    public static readonly All = [
        WorkflowModes.AcousticDesignCycleLite,
        WorkflowModes.AcousticDesignCycle,
        WorkflowModes.FullWFM,
        WorkflowModes.IndustrialHandheld,
        WorkflowModes.IndustrialFixed,
    ];
}

export const ProductSoundDesignWorkflowModes: WorkflowModeType[] = WorkflowModes.All.filter(
    (mode) => !mode.isIndustrial
);

export type WorkflowModeType = (typeof WorkflowModes)[Exclude<keyof typeof WorkflowModes, "All">];
